import ReactDOM from 'react-dom/client'
import App from './App.tsx'
import { ChakraBaseProvider, ColorModeScript } from '@chakra-ui/react'

import theme from './theme/theme.ts'
import { QueryClient, QueryClientProvider } from 'react-query'

import { Helmet } from 'react-helmet'
import React from 'react'
import { Capacitor } from '@capacitor/core'

const HelmetSafe = Helmet as unknown as React.ComponentType<React.PropsWithChildren<Record<string, unknown>>>

const queryClient = new QueryClient()

const DEFAULT_SENTRY_TRACES_SAMPLE_RATE = 0.02

const parseSampleRate = (value: string | undefined, fallback: number) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : fallback
}

// Lazy-load Sentry so it doesn't block the initial render
if (import.meta.env.VITE_IS_LOCAL !== 'true') {
  const SENTRY_DSN_WEB =
    'https://1c2e3341e6611eb39b92ff3f36e96277@o4510234459701248.ingest.us.sentry.io/4510900908720128'
  const SENTRY_DSN_CAPACITOR =
    'https://0ae4c8c44d51bd2c1f3d22c64b4ddaa2@o4510234459701248.ingest.us.sentry.io/4510900944240640'
  const sentryTracesSampleRate = parseSampleRate(
    import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
    DEFAULT_SENTRY_TRACES_SAMPLE_RATE
  )

  import('@sentry/capacitor').then((Sentry) => {
    const isNative = Capacitor.isNativePlatform()
    Sentry.init({
      dsn: isNative ? SENTRY_DSN_CAPACITOR : SENTRY_DSN_WEB,
      environment: import.meta.env.VITE_FIREBASE_ENV,
      sendDefaultPii: true,
      integrations: [
        Sentry.replayIntegration(),
        Sentry.browserTracingIntegration(),
      ],
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1,
      tracesSampleRate: sentryTracesSampleRate,
      tracePropagationTargets: [/^https:\/\/api\.lvbl\.club/],
      denyUrls: [/framer-motion/i, /style-value-types/i],
      ignoreErrors: [
        // Google Maps internal chunk loader failures — transient network errors, not actionable
        /Could not load "(util|places_impl|common|map|marker)"/,
      ],
      beforeSend(event) {
        const exceptionValues = event.exception?.values ?? []
        const messages = [
          event.message,
          ...exceptionValues.map((value) => value.value),
        ].filter(Boolean)
        const frames = exceptionValues.flatMap((value) => value.stacktrace?.frames ?? [])
        const hasGoogleMapsFrame = frames.some((frame) =>
          /^https:\/\/(maps\.googleapis\.com|maps\.gstatic\.com)\//.test(frame.filename ?? '')
        )
        const isTransientGoogleMapsLoadError =
          hasGoogleMapsFrame &&
          messages.some((message) =>
            /^(Load failed|Failed to fetch dynamically imported module|The Google Maps JavaScript API could not load)/.test(
              message ?? ''
            )
          )

        return isTransientGoogleMapsLoadError ? null : event
      },
      enableLogs: true,
    })
  })
}


ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <>
    <HelmetSafe>
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />
    </HelmetSafe>
    <QueryClientProvider client={queryClient}>
      <ColorModeScript initialColorMode={theme.config.initialColorMode} />
      <ChakraBaseProvider theme={theme}>
        <App />
      </ChakraBaseProvider>
    </QueryClientProvider>
  </>,
)
